import type { CalculatorMode } from "@lib/modes";

export enum Token {
    // regular
    NUMBER_0 = "NUMBER_0",
    NUMBER_1 = "NUMBER_1",
    NUMBER_2 = "NUMBER_2",
    NUMBER_3 = "NUMBER_3",
    NUMBER_4 = "NUMBER_4",
    NUMBER_5 = "NUMBER_5",
    NUMBER_6 = "NUMBER_6",
    NUMBER_7 = "NUMBER_7",
    NUMBER_8 = "NUMBER_8",
    NUMBER_9 = "NUMBER_9",
    DECIMAL_POINT = "DECIMAL_POINT",
    SCIENTIFIC_EXPONENTIATION = "SCIENTIFIC_EXPONENTIATION",
    ANSWER = "ANSWER",
    EXECUTION_DELIMITER = "EXECUTION_DELIMITER",
    PLUS = "PLUS",
    MINUS = "MINUS",
    MULTIPLY = "MULTIPLY",
    DIVIDE = "DIVIDE",
    IMAGINARY_UNIT = "IMAGINARY_UNIT",
    LEFT_PARENTHESIS = "LEFT_PARENTHESIS",
    RIGHT_PARENTHESIS = "RIGHT_PARENTHESIS",
    COMMA = "COMMA",
    M_PLUS = "M_PLUS",
    NEGATIVE = "NEGATIVE",
    SEXAGESIMAL_SEPARATOR = "SEXAGESIMAL_SEPARATOR",
    SINE = "SINE",
    COSINE = "COSINE",
    TANGENT = "TANGENT",
    HYPERBOLIC_SINE = "HYPERBOLIC_SINE",
    HYPERBOLIC_COSINE = "HYPERBOLIC_COSINE",
    HYPERBOLIC_TANGENT = "HYPERBOLIC_TANGENT",
    FRACTION = "FRACTION",
    SQUARE_ROOT = "SQUARE_ROOT",
    SQUARE = "SQUARE",
    POWER = "POWER",
    LOGARITHM = "LOGARITHM",
    NATURAL_LOGARITHM = "NATURAL_LOGARITHM",
    INVERSE = "INVERSE",
    CUBE = "CUBE",

    // shift
    ROUND = "ROUND",
    RANDOM = "RANDOM",
    PI = "PI",
    FROM_DEGREE = "FROM_DEGREE",
    FROM_RADIAN = "FROM_RADIAN",
    FROM_GRADIAN = "FROM_GRADIAN",
    POLAR = "POLAR",
    POLAR_COMPLEX = "POLAR_COMPLEX",
    RECTANGULAR = "RECTANGULAR",
    RECTANGULAR_COMPLEX = "RECTANGULAR_COMPLEX",
    PERMUTATION = "PERMUTATION",
    COMBINATION = "COMBINATION",
    STORE = "STORE",
    PERCENT = "PERCENT",
    ARGUMENT = "ARGUMENT",
    ABSOLUTE_VALUE = "ABSOLUTE_VALUE",
    SEMICOLON = "SEMICOLON",
    CONJUGATE = "CONJUGATE",
    M_MINUS = "M_MINUS",
    ANGLE = "ANGLE",
    INVERSE_SINE = "INVERSE_SINE",
    INVERSE_COSINE = "INVERSE_COSINE",
    INVERSE_TANGENT = "INVERSE_TANGENT",
    INVERSE_HYPERBOLIC_SINE = "INVERSE_HYPERBOLIC_SINE",
    INVERSE_HYPERBOLIC_COSINE = "INVERSE_HYPERBOLIC_COSINE",
    INVERSE_HYPERBOLIC_TANGENT = "INVERSE_HYPERBOLIC_TANGENT",
    X_ROOT = "X_ROOT",
    TEN_POWER = "TEN_POWER",
    E_POWER = "E_POWER",
    FACTORIAL = "FACTORIAL",
    CUBE_ROOT = "CUBE_ROOT",

    // alpha
    VARIABLE_A = "VARIABLE_A",
    VARIABLE_B = "VARIABLE_B",
    VARIABLE_C = "VARIABLE_C",
    VARIABLE_D = "VARIABLE_D",
    VARIABLE_X = "VARIABLE_X",
    VARIABLE_Y = "VARIABLE_Y",
    VARIABLE_M = "VARIABLE_M",
    EULER_NUMBER = "EULER_NUMBER",

    // mode 3
    HEXADECIMAL_A = "HEXADECIMAL_A",
    HEXADECIMAL_B = "HEXADECIMAL_B",
    HEXADECIMAL_C = "HEXADECIMAL_C",
    HEXADECIMAL_D = "HEXADECIMAL_D",
    HEXADECIMAL_E = "HEXADECIMAL_E",
    HEXADECIMAL_F = "HEXADECIMAL_F",
    DECIMAL_MODE = "DECIMAL_MODE",
    HEXADECIMAL_MODE = "HEXADECIMAL_MODE",
    BINARY_MODE = "BINARY_MODE",
    OCTAL_MODE = "OCTAL_MODE",
    AND = "AND",
    OR = "OR",
    XNOR = "XNOR",
    XOR = "XOR",
    NOT = "NOT",
    NEGATE = "NEGATE",
    DECIMAL_NUMBER = "DECIMAL_NUMBER",
    HEXADECIMAL_NUMBER = "HEXADECIMAL_NUMBER",
    BINARY_NUMBER = "BINARY_NUMBER",
    OCTAL_NUMBER = "OCTAL_NUMBER",

    // mode 4 & 5
    ADD_DATA_POINT = "ADD_DATA_POINT",
    X_SQUARE_SUMMATION = "X_SQUARE_SUMMATION",
    X_SUMMATION = "X_SUMMATION",
    NUMBER_OF_DATA = "NUMBER_OF_DATA",
    Y_SQUARE_SUMMATION = "Y_SQUARE_SUMMATION",
    Y_SUMMATION = "Y_SUMMATION",
    XY_SUMMATION = "XY_SUMMATION",
    X_SQUARE_Y_SUMMATION = "X_SQUARE_Y_SUMMATION",
    X_CUBE_SUMMATION = "X_CUBE_SUMMATION",
    X_QUADRUPLE_SUMMATION = "X_QUADRUPLE_SUMMATION",
    X_MEAN = "X_MEAN",
    X_STANDARD_DEVIATION = "X_STANDARD_DEVIATION",
    X_SAMPLE_STANDARD_DEVIATION = "X_SAMPLE_STANDARD_DEVIATION",
    Y_MEAN = "Y_MEAN",
    Y_STANDARD_DEVIATION = "Y_STANDARD_DEVIATION",
    Y_SAMPLE_STANDARD_DEVIATION = "Y_SAMPLE_STANDARD_DEVIATION",
    REGRESSION_CONSTANT_A = "REGRESSION_CONSTANT_A",
    REGRESSION_COEFFICIENT_B = "REGRESSION_COEFFICIENT_B",
    REGRESSION_COEFFICIENT_C = "REGRESSION_COEFFICIENT_C",
    REGRESSION_CORRELATION_COEFFICIENT_R = "REGRESSION_CORRELATION_COEFFICIENT_R",
    X_ESTIMATED_VALUE = "X_ESTIMATED_VALUE",
    X1_ESTIMATED_VALUE = "X1_ESTIMATED_VALUE",
    X2_ESTIMATED_VALUE = "X2_ESTIMATED_VALUE",
    Y_ESTIMATED_VALUE = "Y_ESTIMATED_VALUE",
    X_MINIMUM = "X_MINIMUM",
    X_MAXIMUM = "X_MAXIMUM",
    Y_MINIMUM = "Y_MINIMUM",
    Y_MAXIMUM = "Y_MAXIMUM",

    // constants
    PROTON_MASS = "PROTON_MASS",
    NEUTRON_MASS = "NEUTRON_MASS",
    ELECTRON_MASS = "ELECTRON_MASS",
    MUON_MASS = "MUON_MASS",
    BOHR_RADIUS = "BOHR_RADIUS",
    PLANCK_CONSTANT = "PLANCK_CONSTANT",
    NUCLEAR_MAGNETON = "NUCLEAR_MAGNETON",
    BOHR_MAGNETON = "BOHR_MAGNETON",
    PLANCK_CONSTANT_REDUCED = "PLANCK_CONSTANT_RATIONALISED",
    FINE_STRUCTURE_CONSTANT = "FINE_STRUCTURE_CONSTANT",
    CLASSICAL_ELECTRON_RADIUS = "CLASSICAL_ELECTRON_RADIUS",
    ELECTRON_COMPTON_WAVELENGTH = "ELECTRON_COMPTON_WAVELENGTH",
    PROTON_GYROMAGNETIC_RATIO = "PROTON_GYROMAGNETIC_RATIO",
    PROTON_COMPTON_WAVELENGTH = "PROTON_COMPTON_WAVELENGTH",
    NEUTRON_COMPTON_WAVELENGTH = "NEUTRON_COMPTON_WAVELENGTH",
    RYDBERG_CONSTANT = "RYDBERG_CONSTANT",
    ATOMIC_MASS_CONSTANT = "ATOMIC_MASS_CONSTANT",
    PROTON_MAGNETIC_MOMENT = "PROTON_MAGNETIC_MOMENT",
    ELECTRON_MAGNETIC_MOMENT = "ELECTRON_MAGNETIC_MOMENT",
    NEUTRON_MAGNETIC_MOMENT = "NEUTRON_MAGNETIC_MOMENT",
    MUON_MAGNETIC_MOMENT = "MUON_MAGNETIC_MOMENT",
    FARADAY_CONSTANT = "FARADAY_CONSTANT",
    ELEMENTARY_CHARGE = "ELEMENTARY_CHARGE",
    AVOGADRO_CONSTANT = "AVOGADRO_CONSTANT",
    BOLTZMANN_CONSTANT = "BOLTZMANN_CONSTANT",
    MOLAR_VOLUME_OF_IDEAL_GAS = "MOLAR_VOLUME_OF_IDEAL_GAS",
    MOLAR_GAS_CONSTANT = "MOLAR_GAS_CONSTANT",
    SPEED_OF_LIGHT_IN_VACUUM = "SPEED_OF_LIGHT_IN_VACUUM",
    FIRST_RADIATION_CONSTANT = "FIRST_RADIATION_CONSTANT",
    SECOND_RADIATION_CONSTANT = "SECOND_RADIATION_CONSTANT",
    STEFAN_BOLTZMANN_CONSTANT = "STEFAN_BOLTZMANN_CONSTANT",
    VACUUM_PERMITTIVITY = "VACUUM_PERMITTIVITY",
    VACUUM_PERMEABILITY = "VACUUM_PERMEABILITY",
    MAGNETIC_FLUX_QUANTUM = "MAGNETIC_FLUX_QUANTUM",
    STANDARD_ACCERLATION_OF_GRAVITY = "STANDARD_ACCERLATION_OF_GRAVITY",
    CONDUCTANCE_QUANTUM = "CONDUCTANCE_QUANTUM",
    CHARACTERISTIC_IMPEDANCE_OF_VACUUM = "CHARACTERISTIC_IMPEDANCE_OF_VACUUM",
    KELVIN_CELSIUS_SCALE_OFFSET = "KELVIN_CELSIUS_SCALE_OFFSET",
    GRAVITATIONAL_CONSTANT = "GRAVITATIONAL_CONSTANT",
    STANDARD_ATMOSPHERE = "STANDARD_ATMOSPHERE",

    // clear
    CLEAR_MEMORY = "CLEAR_MEMORY",
    CLEAR_STATISTICS = "CLEAR_STATISTICS",

    // setup
    DEGREE_MODE = "DEGREE_MODE",
    RADIAN_MODE = "RADIAN_MODE",
    GRADIAN_MODE = "GRADIAN_MODE",
    FIXED_POINT = "FIXED_POINT",
    SCIENTIFIC_NOTATION = "SCIENTIFIC_NOTATION",
    NORMAL_NOTATION = "NORMAL_NOTATION",
    FREQUENCY_ON = "FREQUENCY_ON",
    FREQUENCY_OFF = "FREQUENCY_OFF",

    // program control
    QUERY_INPUT = "QUERY_INPUT",
    DISPLAY = "DISPLAY",
    CONDITIONAL = "CONDITIONAL",
    EQUAL = "EQUAL",
    NOT_EQUAL = "NOT_EQUAL",
    GREATER_THAN = "GREATER_THAN",
    LESS_THAN = "LESS_THAN",
    GREATER_THAN_OR_EQUAL = "GREATER_THAN_OR_EQUAL",
    LESS_THAN_OR_EQUAL = "LESS_THAN_OR_EQUAL",
    GOTO = "GOTO",
    LABEL = "LABEL",
    WHILE = "WHILE",
    WHILE_END = "WHILE_END",
    NEXT = "NEXT",
    BREAK = "BREAK",
    FOR = "FOR",
    TO = "TO",
    STEP = "STEP",
    ELSE = "ELSE",
    IF_END = "IF_END",
    IF = "IF",
    THEN = "THEN",
}

export const TokenAvailableMode: Record<Token, CalculatorMode[]> = {
    [Token.NUMBER_0]: [1, 2, 3, 4, 5],
    [Token.NUMBER_1]: [1, 2, 3, 4, 5],
    [Token.NUMBER_2]: [1, 2, 3, 4, 5],
    [Token.NUMBER_3]: [1, 2, 3, 4, 5],
    [Token.NUMBER_4]: [1, 2, 3, 4, 5],
    [Token.NUMBER_5]: [1, 2, 3, 4, 5],
    [Token.NUMBER_6]: [1, 2, 3, 4, 5],
    [Token.NUMBER_7]: [1, 2, 3, 4, 5],
    [Token.NUMBER_8]: [1, 2, 3, 4, 5],
    [Token.NUMBER_9]: [1, 2, 3, 4, 5],
    [Token.DECIMAL_POINT]: [1, 2, 4, 5],
    [Token.SCIENTIFIC_EXPONENTIATION]: [1, 2, 4, 5],
    [Token.ANSWER]: [1, 2, 3, 4, 5],
    [Token.EXECUTION_DELIMITER]: [1, 2, 3, 4, 5],
    [Token.PLUS]: [1, 2, 3, 4, 5],
    [Token.MINUS]: [1, 2, 3, 4, 5],
    [Token.MULTIPLY]: [1, 2, 3, 4, 5],
    [Token.DIVIDE]: [1, 2, 3, 4, 5],
    [Token.IMAGINARY_UNIT]: [2],
    [Token.LEFT_PARENTHESIS]: [1, 2, 3, 4, 5],
    [Token.RIGHT_PARENTHESIS]: [1, 2, 3, 4, 5],
    [Token.COMMA]: [1, 2, 4, 5],
    [Token.M_PLUS]: [1, 2, 3, 4, 5],
    [Token.NEGATIVE]: [1, 2, 4, 5],
    [Token.SEXAGESIMAL_SEPARATOR]: [1, 2, 4, 5],
    [Token.SINE]: [1, 2, 4, 5],
    [Token.COSINE]: [1, 2, 4, 5],
    [Token.TANGENT]: [1, 2, 4, 5],
    [Token.HYPERBOLIC_SINE]: [1, 2, 4, 5],
    [Token.HYPERBOLIC_COSINE]: [1, 2, 4, 5],
    [Token.HYPERBOLIC_TANGENT]: [1, 2, 4, 5],
    [Token.FRACTION]: [1, 2, 4, 5],
    [Token.SQUARE_ROOT]: [1, 2, 4, 5],
    [Token.SQUARE]: [1, 2, 4, 5],
    [Token.POWER]: [1, 2, 4, 5],
    [Token.LOGARITHM]: [1, 2, 4, 5],
    [Token.NATURAL_LOGARITHM]: [1, 2, 4, 5],
    [Token.INVERSE]: [1, 2, 4, 5],
    [Token.CUBE]: [1, 2, 4, 5],
    [Token.ROUND]: [1, 2, 4, 5],
    [Token.RANDOM]: [1, 2, 4, 5],
    [Token.PI]: [1, 2, 4, 5],
    [Token.FROM_DEGREE]: [1, 2, 4, 5],
    [Token.FROM_RADIAN]: [1, 2, 4, 5],
    [Token.FROM_GRADIAN]: [1, 2, 4, 5],
    [Token.POLAR]: [1, 4, 5],
    [Token.POLAR_COMPLEX]: [2],
    [Token.RECTANGULAR]: [1, 4, 5],
    [Token.RECTANGULAR_COMPLEX]: [2],
    [Token.PERMUTATION]: [1, 2, 4, 5],
    [Token.COMBINATION]: [1, 2, 4, 5],
    [Token.STORE]: [1, 2, 3, 4, 5],
    [Token.PERCENT]: [1, 4, 5],
    [Token.ARGUMENT]: [2],
    [Token.ABSOLUTE_VALUE]: [1, 2, 4, 5],
    [Token.SEMICOLON]: [1, 4, 5],
    [Token.CONJUGATE]: [2],
    [Token.M_MINUS]: [1, 2, 3],
    [Token.ANGLE]: [2],
    [Token.INVERSE_SINE]: [1, 2, 4, 5],
    [Token.INVERSE_COSINE]: [1, 2, 4, 5],
    [Token.INVERSE_TANGENT]: [1, 2, 4, 5],
    [Token.INVERSE_HYPERBOLIC_SINE]: [1, 2, 4, 5],
    [Token.INVERSE_HYPERBOLIC_COSINE]: [1, 2, 4, 5],
    [Token.INVERSE_HYPERBOLIC_TANGENT]: [1, 2, 4, 5],
    [Token.X_ROOT]: [1, 2, 4, 5],
    [Token.TEN_POWER]: [1, 2, 4, 5],
    [Token.E_POWER]: [1, 2, 4, 5],
    [Token.FACTORIAL]: [1, 2, 4, 5],
    [Token.CUBE_ROOT]: [1, 2, 4, 5],
    [Token.VARIABLE_A]: [1, 2, 3, 4, 5],
    [Token.VARIABLE_B]: [1, 2, 3, 4, 5],
    [Token.VARIABLE_C]: [1, 2, 3, 4, 5],
    [Token.VARIABLE_D]: [1, 2, 3, 4, 5],
    [Token.VARIABLE_X]: [1, 2, 3, 4, 5],
    [Token.VARIABLE_Y]: [1, 2, 3, 4, 5],
    [Token.VARIABLE_M]: [1, 2, 3, 4, 5],
    [Token.EULER_NUMBER]: [1, 2, 4, 5],
    [Token.HEXADECIMAL_A]: [3],
    [Token.HEXADECIMAL_B]: [3],
    [Token.HEXADECIMAL_C]: [3],
    [Token.HEXADECIMAL_D]: [3],
    [Token.HEXADECIMAL_E]: [3],
    [Token.HEXADECIMAL_F]: [3],
    [Token.DECIMAL_MODE]: [3],
    [Token.HEXADECIMAL_MODE]: [3],
    [Token.BINARY_MODE]: [3],
    [Token.OCTAL_MODE]: [3],
    [Token.AND]: [3],
    [Token.OR]: [3],
    [Token.XNOR]: [3],
    [Token.XOR]: [3],
    [Token.NOT]: [3],
    [Token.NEGATE]: [3],
    [Token.DECIMAL_NUMBER]: [3],
    [Token.HEXADECIMAL_NUMBER]: [3],
    [Token.BINARY_NUMBER]: [3],
    [Token.OCTAL_NUMBER]: [3],
    [Token.ADD_DATA_POINT]: [4, 5],
    [Token.X_SQUARE_SUMMATION]: [4, 5],
    [Token.X_SUMMATION]: [4, 5],
    [Token.NUMBER_OF_DATA]: [4, 5],
    [Token.Y_SQUARE_SUMMATION]: [5],
    [Token.Y_SUMMATION]: [5],
    [Token.XY_SUMMATION]: [5],
    [Token.X_SQUARE_Y_SUMMATION]: [5],
    [Token.X_CUBE_SUMMATION]: [5],
    [Token.X_QUADRUPLE_SUMMATION]: [5],
    [Token.X_MEAN]: [4, 5],
    [Token.X_STANDARD_DEVIATION]: [4, 5],
    [Token.X_SAMPLE_STANDARD_DEVIATION]: [4, 5],
    [Token.Y_MEAN]: [5],
    [Token.Y_STANDARD_DEVIATION]: [5],
    [Token.Y_SAMPLE_STANDARD_DEVIATION]: [5],
    [Token.REGRESSION_CONSTANT_A]: [5],
    [Token.REGRESSION_COEFFICIENT_B]: [5],
    [Token.REGRESSION_COEFFICIENT_C]: [5],
    [Token.REGRESSION_CORRELATION_COEFFICIENT_R]: [5],
    [Token.X_ESTIMATED_VALUE]: [5],
    [Token.X1_ESTIMATED_VALUE]: [5],
    [Token.X2_ESTIMATED_VALUE]: [5],
    [Token.Y_ESTIMATED_VALUE]: [5],
    [Token.X_MINIMUM]: [4, 5],
    [Token.X_MAXIMUM]: [4, 5],
    [Token.Y_MINIMUM]: [5],
    [Token.Y_MAXIMUM]: [5],
    [Token.PROTON_MASS]: [1, 2, 4, 5],
    [Token.NEUTRON_MASS]: [1, 2, 4, 5],
    [Token.ELECTRON_MASS]: [1, 2, 4, 5],
    [Token.MUON_MASS]: [1, 2, 4, 5],
    [Token.BOHR_RADIUS]: [1, 2, 4, 5],
    [Token.PLANCK_CONSTANT]: [1, 2, 4, 5],
    [Token.NUCLEAR_MAGNETON]: [1, 2, 4, 5],
    [Token.BOHR_MAGNETON]: [1, 2, 4, 5],
    [Token.PLANCK_CONSTANT_REDUCED]: [1, 2, 4, 5],
    [Token.FINE_STRUCTURE_CONSTANT]: [1, 2, 4, 5],
    [Token.CLASSICAL_ELECTRON_RADIUS]: [1, 2, 4, 5],
    [Token.ELECTRON_COMPTON_WAVELENGTH]: [1, 2, 4, 5],
    [Token.PROTON_GYROMAGNETIC_RATIO]: [1, 2, 4, 5],
    [Token.PROTON_COMPTON_WAVELENGTH]: [1, 2, 4, 5],
    [Token.NEUTRON_COMPTON_WAVELENGTH]: [1, 2, 4, 5],
    [Token.RYDBERG_CONSTANT]: [1, 2, 4, 5],
    [Token.ATOMIC_MASS_CONSTANT]: [1, 2, 4, 5],
    [Token.PROTON_MAGNETIC_MOMENT]: [1, 2, 4, 5],
    [Token.NEUTRON_MAGNETIC_MOMENT]: [1, 2, 4, 5],
    [Token.ELECTRON_MAGNETIC_MOMENT]: [1, 2, 4, 5],
    [Token.MUON_MAGNETIC_MOMENT]: [1, 2, 4, 5],
    [Token.FARADAY_CONSTANT]: [1, 2, 4, 5],
    [Token.ELEMENTARY_CHARGE]: [1, 2, 4, 5],
    [Token.AVOGADRO_CONSTANT]: [1, 2, 4, 5],
    [Token.BOLTZMANN_CONSTANT]: [1, 2, 4, 5],
    [Token.MOLAR_VOLUME_OF_IDEAL_GAS]: [1, 2, 4, 5],
    [Token.MOLAR_GAS_CONSTANT]: [1, 2, 4, 5],
    [Token.SPEED_OF_LIGHT_IN_VACUUM]: [1, 2, 4, 5],
    [Token.FIRST_RADIATION_CONSTANT]: [1, 2, 4, 5],
    [Token.SECOND_RADIATION_CONSTANT]: [1, 2, 4, 5],
    [Token.STEFAN_BOLTZMANN_CONSTANT]: [1, 2, 4, 5],
    [Token.VACUUM_PERMITTIVITY]: [1, 2, 4, 5],
    [Token.VACUUM_PERMEABILITY]: [1, 2, 4, 5],
    [Token.MAGNETIC_FLUX_QUANTUM]: [1, 2, 4, 5],
    [Token.STANDARD_ACCERLATION_OF_GRAVITY]: [1, 2, 4, 5],
    [Token.CONDUCTANCE_QUANTUM]: [1, 2, 4, 5],
    [Token.CHARACTERISTIC_IMPEDANCE_OF_VACUUM]: [1, 2, 4, 5],
    [Token.KELVIN_CELSIUS_SCALE_OFFSET]: [1, 2, 4, 5],
    [Token.GRAVITATIONAL_CONSTANT]: [1, 2, 4, 5],
    [Token.STANDARD_ATMOSPHERE]: [1, 2, 4, 5],
    [Token.CLEAR_MEMORY]: [1, 2, 3],
    [Token.CLEAR_STATISTICS]: [4, 5],
    [Token.DEGREE_MODE]: [1, 2, 4, 5],
    [Token.RADIAN_MODE]: [1, 2, 4, 5],
    [Token.GRADIAN_MODE]: [1, 2, 4, 5],
    [Token.FIXED_POINT]: [1, 2, 3, 4, 5],
    [Token.SCIENTIFIC_NOTATION]: [1, 2, 3, 4, 5],
    [Token.NORMAL_NOTATION]: [1, 2, 3, 4, 5],
    [Token.FREQUENCY_ON]: [1, 2, 3, 4, 5],
    [Token.FREQUENCY_OFF]: [1, 2, 3, 4, 5],
    [Token.QUERY_INPUT]: [1, 2, 3, 4, 5],
    [Token.DISPLAY]: [1, 2, 3, 4, 5],
    [Token.CONDITIONAL]: [1, 2, 3, 4, 5],
    [Token.EQUAL]: [1, 2, 3, 4, 5],
    [Token.NOT_EQUAL]: [1, 2, 3, 4, 5],
    [Token.GREATER_THAN]: [1, 2, 3, 4, 5],
    [Token.LESS_THAN]: [1, 2, 3, 4, 5],
    [Token.GREATER_THAN_OR_EQUAL]: [1, 2, 3, 4, 5],
    [Token.LESS_THAN_OR_EQUAL]: [1, 2, 3, 4, 5],
    [Token.GOTO]: [1, 2, 3, 4, 5],
    [Token.LABEL]: [1, 2, 3, 4, 5],
    [Token.WHILE]: [1, 2, 3, 4, 5],
    [Token.WHILE_END]: [1, 2, 3, 4, 5],
    [Token.NEXT]: [1, 2, 3, 4, 5],
    [Token.BREAK]: [1, 2, 3, 4, 5],
    [Token.FOR]: [1, 2, 3, 4, 5],
    [Token.TO]: [1, 2, 3, 4, 5],
    [Token.STEP]: [1, 2, 3, 4, 5],
    [Token.ELSE]: [1, 2, 3, 4, 5],
    [Token.IF_END]: [1, 2, 3, 4, 5],
    [Token.IF]: [1, 2, 3, 4, 5],
    [Token.THEN]: [1, 2, 3, 4, 5],
} as const;

export const TokenSymbol: Record<Token, string> = {
    [Token.NUMBER_0]: "0",
    [Token.NUMBER_1]: "1",
    [Token.NUMBER_2]: "2",
    [Token.NUMBER_3]: "3",
    [Token.NUMBER_4]: "4",
    [Token.NUMBER_5]: "5",
    [Token.NUMBER_6]: "6",
    [Token.NUMBER_7]: "7",
    [Token.NUMBER_8]: "8",
    [Token.NUMBER_9]: "9",
    [Token.DECIMAL_POINT]: ".",
    [Token.SCIENTIFIC_EXPONENTIATION]: "E",
    [Token.ANSWER]: "Ans",
    [Token.EXECUTION_DELIMITER]: ":",
    [Token.PLUS]: "+",
    [Token.MINUS]: "-",
    [Token.MULTIPLY]: "×",
    [Token.DIVIDE]: "÷",
    [Token.IMAGINARY_UNIT]: "i",
    [Token.LEFT_PARENTHESIS]: "(",
    [Token.RIGHT_PARENTHESIS]: ")",
    [Token.COMMA]: ",",
    [Token.M_PLUS]: "M+",
    [Token.NEGATIVE]: "−",
    [Token.SEXAGESIMAL_SEPARATOR]: "⁰",
    [Token.SINE]: "sin(",
    [Token.COSINE]: "cos(",
    [Token.TANGENT]: "tan(",
    [Token.HYPERBOLIC_SINE]: "sinh(",
    [Token.HYPERBOLIC_COSINE]: "cosh(",
    [Token.HYPERBOLIC_TANGENT]: "tanh(",
    [Token.FRACTION]: "⌟",
    [Token.SQUARE_ROOT]: "√(",
    [Token.SQUARE]: "²",
    [Token.POWER]: "^(",
    [Token.LOGARITHM]: "log(",
    [Token.NATURAL_LOGARITHM]: "ln(",
    [Token.INVERSE]: "⁻¹",
    [Token.CUBE]: "³",
    [Token.ROUND]: "Rnd(",
    [Token.RANDOM]: "Ran#",
    [Token.PI]: "π",
    [Token.FROM_DEGREE]: "°",
    [Token.FROM_RADIAN]: "ʳ",
    [Token.FROM_GRADIAN]: "ᵍ",
    [Token.POLAR]: "Pol(",
    [Token.POLAR_COMPLEX]: "▸r∠θ",
    [Token.RECTANGULAR]: "Rec(",
    [Token.RECTANGULAR_COMPLEX]: "▸a+bi",
    [Token.PERMUTATION]: "ℙ",
    [Token.COMBINATION]: "ℂ",
    [Token.STORE]: "→",
    [Token.PERCENT]: "%",
    [Token.ARGUMENT]: "arg(",
    [Token.ABSOLUTE_VALUE]: "Abs(",
    [Token.SEMICOLON]: ";",
    [Token.CONJUGATE]: "Conjg(",
    [Token.M_MINUS]: "M-",
    [Token.ANGLE]: "∠",
    [Token.INVERSE_SINE]: "sin⁻¹(",
    [Token.INVERSE_COSINE]: "cos⁻¹(",
    [Token.INVERSE_TANGENT]: "tan⁻¹(",
    [Token.INVERSE_HYPERBOLIC_SINE]: "sinh⁻¹(",
    [Token.INVERSE_HYPERBOLIC_COSINE]: "cosh⁻¹(",
    [Token.INVERSE_HYPERBOLIC_TANGENT]: "tanh⁻¹(",
    [Token.X_ROOT]: "ˣ√(",
    [Token.TEN_POWER]: "₁₀^(",
    [Token.E_POWER]: "e^(",
    [Token.FACTORIAL]: "!",
    [Token.CUBE_ROOT]: "∛(",
    [Token.VARIABLE_A]: "A",
    [Token.VARIABLE_B]: "B",
    [Token.VARIABLE_C]: "C",
    [Token.VARIABLE_D]: "D",
    [Token.VARIABLE_X]: "X",
    [Token.VARIABLE_Y]: "Y",
    [Token.VARIABLE_M]: "M",
    [Token.EULER_NUMBER]: "𝓮",
    [Token.HEXADECIMAL_A]: "𝙰",
    [Token.HEXADECIMAL_B]: "𝙱",
    [Token.HEXADECIMAL_C]: "𝙲",
    [Token.HEXADECIMAL_D]: "𝙳",
    [Token.HEXADECIMAL_E]: "𝙴",
    [Token.HEXADECIMAL_F]: "𝙵",
    [Token.DECIMAL_MODE]: "Dec",
    [Token.HEXADECIMAL_MODE]: "Hex",
    [Token.BINARY_MODE]: "Bin",
    [Token.OCTAL_MODE]: "Oct",
    [Token.AND]: "and",
    [Token.OR]: "or",
    [Token.XNOR]: "xnor",
    [Token.XOR]: "xor",
    [Token.NOT]: "Not",
    [Token.NEGATE]: "Neg",
    [Token.DECIMAL_NUMBER]: "𝗱",
    [Token.HEXADECIMAL_NUMBER]: "𝗵",
    [Token.BINARY_NUMBER]: "𝗯",
    [Token.OCTAL_NUMBER]: "𝗼",
    [Token.ADD_DATA_POINT]: "DT",
    [Token.X_SQUARE_SUMMATION]: "Σx²",
    [Token.X_SUMMATION]: "Σx",
    [Token.NUMBER_OF_DATA]: "n",
    [Token.Y_SQUARE_SUMMATION]: "Σy²",
    [Token.Y_SUMMATION]: "Σy",
    [Token.XY_SUMMATION]: "Σxy",
    [Token.X_SQUARE_Y_SUMMATION]: "Σx²y",
    [Token.X_CUBE_SUMMATION]: "Σx³",
    [Token.X_QUADRUPLE_SUMMATION]: "Σx⁴",
    [Token.X_MEAN]: "x̄",
    [Token.X_STANDARD_DEVIATION]: "σₓ",
    [Token.X_SAMPLE_STANDARD_DEVIATION]: "sₓ",
    [Token.Y_MEAN]: "ȳ",
    [Token.Y_STANDARD_DEVIATION]: "σᵧ",
    [Token.Y_SAMPLE_STANDARD_DEVIATION]: "sᵧ",
    [Token.REGRESSION_CONSTANT_A]: "a",
    [Token.REGRESSION_COEFFICIENT_B]: "b",
    [Token.REGRESSION_COEFFICIENT_C]: "c",
    [Token.REGRESSION_CORRELATION_COEFFICIENT_R]: "r",
    [Token.X_ESTIMATED_VALUE]: "x̂",
    [Token.X1_ESTIMATED_VALUE]: "x̂₁",
    [Token.X2_ESTIMATED_VALUE]: "x̂₂",
    [Token.Y_ESTIMATED_VALUE]: "ŷ",
    [Token.X_MINIMUM]: "minX",
    [Token.X_MAXIMUM]: "maxX",
    [Token.Y_MINIMUM]: "minY",
    [Token.Y_MAXIMUM]: "maxY",
    [Token.PROTON_MASS]: "mₚ",
    [Token.NEUTRON_MASS]: "mₙ",
    [Token.ELECTRON_MASS]: "mₑ",
    [Token.MUON_MASS]: "m_μ",
    [Token.BOHR_RADIUS]: "a₀",
    [Token.PLANCK_CONSTANT]: "h",
    [Token.NUCLEAR_MAGNETON]: "μɴ",
    [Token.BOHR_MAGNETON]: "μʙ",
    [Token.PLANCK_CONSTANT_REDUCED]: "ħ",
    [Token.FINE_STRUCTURE_CONSTANT]: "α",
    [Token.CLASSICAL_ELECTRON_RADIUS]: "rₑ",
    [Token.ELECTRON_COMPTON_WAVELENGTH]: "λ꜀",
    [Token.PROTON_GYROMAGNETIC_RATIO]: "γₚ",
    [Token.PROTON_COMPTON_WAVELENGTH]: "λ꜀ₚ",
    [Token.NEUTRON_COMPTON_WAVELENGTH]: "λ꜀ₙ",
    [Token.RYDBERG_CONSTANT]: "R_∞",
    [Token.ATOMIC_MASS_CONSTANT]: "u",
    [Token.PROTON_MAGNETIC_MOMENT]: "μₚ",
    [Token.ELECTRON_MAGNETIC_MOMENT]: "μₑ",
    [Token.NEUTRON_MAGNETIC_MOMENT]: "μₙ",
    [Token.MUON_MAGNETIC_MOMENT]: "μ_μ",
    [Token.FARADAY_CONSTANT]: "F",
    [Token.ELEMENTARY_CHARGE]: "e",
    [Token.AVOGADRO_CONSTANT]: "Nᴀ",
    [Token.BOLTZMANN_CONSTANT]: "k",
    [Token.MOLAR_VOLUME_OF_IDEAL_GAS]: "Vₘ",
    [Token.MOLAR_GAS_CONSTANT]: "R",
    [Token.SPEED_OF_LIGHT_IN_VACUUM]: "c₀",
    [Token.FIRST_RADIATION_CONSTANT]: "c₁",
    [Token.SECOND_RADIATION_CONSTANT]: "c₂",
    [Token.STEFAN_BOLTZMANN_CONSTANT]: "σ",
    [Token.VACUUM_PERMITTIVITY]: "ε₀",
    [Token.VACUUM_PERMEABILITY]: "μ₀",
    [Token.MAGNETIC_FLUX_QUANTUM]: "Φ₀",
    [Token.STANDARD_ACCERLATION_OF_GRAVITY]: "g",
    [Token.CONDUCTANCE_QUANTUM]: "G₀",
    [Token.CHARACTERISTIC_IMPEDANCE_OF_VACUUM]: "Z₀",
    [Token.KELVIN_CELSIUS_SCALE_OFFSET]: "t",
    [Token.GRAVITATIONAL_CONSTANT]: "G",
    [Token.STANDARD_ATMOSPHERE]: "atm",
    [Token.CLEAR_MEMORY]: "ClrMemory",
    [Token.CLEAR_STATISTICS]: "ClrStat",
    [Token.DEGREE_MODE]: "Deg",
    [Token.RADIAN_MODE]: "Rad",
    [Token.GRADIAN_MODE]: "Gra",
    [Token.FIXED_POINT]: "Fix",
    [Token.SCIENTIFIC_NOTATION]: "Sci",
    [Token.NORMAL_NOTATION]: "Norm",
    [Token.FREQUENCY_ON]: "FreqOn",
    [Token.FREQUENCY_OFF]: "FreqOff",
    [Token.QUERY_INPUT]: "?",
    [Token.DISPLAY]: "◢",
    [Token.CONDITIONAL]: "⇒",
    [Token.EQUAL]: "=",
    [Token.NOT_EQUAL]: "≠",
    [Token.GREATER_THAN]: ">",
    [Token.LESS_THAN]: "<",
    [Token.GREATER_THAN_OR_EQUAL]: "≥",
    [Token.LESS_THAN_OR_EQUAL]: "≤",
    [Token.GOTO]: "Goto",
    [Token.LABEL]: "Lbl",
    [Token.WHILE]: "While",
    [Token.WHILE_END]: "WhileEnd",
    [Token.NEXT]: "Next",
    [Token.BREAK]: "Break",
    [Token.FOR]: "For",
    [Token.TO]: "To",
    [Token.STEP]: "Step",
    [Token.ELSE]: "Else",
    [Token.IF_END]: "IfEnd",
    [Token.IF]: "If",
    [Token.THEN]: "Then",
} as const;

export const TokenButton: Record<Token, string> = {
    [Token.NUMBER_0]: "0",
    [Token.NUMBER_1]: "1",
    [Token.NUMBER_2]: "2",
    [Token.NUMBER_3]: "3",
    [Token.NUMBER_4]: "4",
    [Token.NUMBER_5]: "5",
    [Token.NUMBER_6]: "6",
    [Token.NUMBER_7]: "7",
    [Token.NUMBER_8]: "8",
    [Token.NUMBER_9]: "9",
    [Token.DECIMAL_POINT]: ".",
    [Token.SCIENTIFIC_EXPONENTIATION]: "EXP",
    [Token.ANSWER]: "Ans",
    [Token.EXECUTION_DELIMITER]: "EXE",
    [Token.PLUS]: "+",
    [Token.MINUS]: "-",
    [Token.MULTIPLY]: "×",
    [Token.DIVIDE]: "÷",
    [Token.IMAGINARY_UNIT]: "i",
    [Token.LEFT_PARENTHESIS]: "(",
    [Token.RIGHT_PARENTHESIS]: ")",
    [Token.COMMA]: ",",
    [Token.M_PLUS]: "M+",
    [Token.NEGATIVE]: "(-)",
    [Token.SEXAGESIMAL_SEPARATOR]: "°’”",
    [Token.SINE]: "sin",
    [Token.COSINE]: "cos",
    [Token.TANGENT]: "tan",
    [Token.HYPERBOLIC_SINE]: "sin",
    [Token.HYPERBOLIC_COSINE]: "cos",
    [Token.HYPERBOLIC_TANGENT]: "tan",
    [Token.FRACTION]: "aᵇ/꜀",
    [Token.SQUARE_ROOT]: "√",
    [Token.SQUARE]: "x²",
    [Token.POWER]: "^",
    [Token.LOGARITHM]: "log",
    [Token.NATURAL_LOGARITHM]: "ln",
    [Token.INVERSE]: "x⁻¹",
    [Token.CUBE]: "x³",
    [Token.ROUND]: "Rnd",
    [Token.RANDOM]: "Ran#",
    [Token.PI]: "π",
    [Token.FROM_DEGREE]: "DRG▸",
    [Token.FROM_RADIAN]: "DRG▸",
    [Token.FROM_GRADIAN]: "DRG▸",
    [Token.POLAR]: "Pol",
    [Token.POLAR_COMPLEX]: "▸r∠θ",
    [Token.RECTANGULAR]: "Rec",
    [Token.RECTANGULAR_COMPLEX]: "▸a+bi",
    [Token.PERMUTATION]: "nPr",
    [Token.COMBINATION]: "nCr",
    [Token.STORE]: "STO",
    [Token.PERCENT]: "%",
    [Token.ARGUMENT]: "arg",
    [Token.ABSOLUTE_VALUE]: "Abs",
    [Token.SEMICOLON]: ";",
    [Token.CONJUGATE]: "Conjg",
    [Token.M_MINUS]: "M-",
    [Token.ANGLE]: "∠",
    [Token.INVERSE_SINE]: "sin⁻¹",
    [Token.INVERSE_COSINE]: "cos⁻¹",
    [Token.INVERSE_TANGENT]: "tan⁻¹",
    [Token.INVERSE_HYPERBOLIC_SINE]: "sin⁻¹",
    [Token.INVERSE_HYPERBOLIC_COSINE]: "cos⁻¹",
    [Token.INVERSE_HYPERBOLIC_TANGENT]: "tan⁻¹",
    [Token.X_ROOT]: "ˣ√",
    [Token.TEN_POWER]: "10ˣ",
    [Token.E_POWER]: "eˣ",
    [Token.FACTORIAL]: "x!",
    [Token.CUBE_ROOT]: "∛",
    [Token.VARIABLE_A]: "A",
    [Token.VARIABLE_B]: "B",
    [Token.VARIABLE_C]: "C",
    [Token.VARIABLE_D]: "D",
    [Token.VARIABLE_X]: "X",
    [Token.VARIABLE_Y]: "Y",
    [Token.VARIABLE_M]: "M",
    [Token.EULER_NUMBER]: "e",
    [Token.HEXADECIMAL_A]: "A",
    [Token.HEXADECIMAL_B]: "B",
    [Token.HEXADECIMAL_C]: "C",
    [Token.HEXADECIMAL_D]: "D",
    [Token.HEXADECIMAL_E]: "E",
    [Token.HEXADECIMAL_F]: "F",
    [Token.DECIMAL_MODE]: "DEC",
    [Token.HEXADECIMAL_MODE]: "HEX",
    [Token.BINARY_MODE]: "BIN",
    [Token.OCTAL_MODE]: "OCT",
    [Token.AND]: "LOGIC",
    [Token.OR]: "LOGIC",
    [Token.XNOR]: "LOGIC",
    [Token.XOR]: "LOGIC",
    [Token.NOT]: "LOGIC",
    [Token.NEGATE]: "LOGIC",
    [Token.DECIMAL_NUMBER]: "LOGIC",
    [Token.HEXADECIMAL_NUMBER]: "LOGIC",
    [Token.BINARY_NUMBER]: "LOGIC",
    [Token.OCTAL_NUMBER]: "LOGIC",
    [Token.ADD_DATA_POINT]: "DT",
    [Token.X_SQUARE_SUMMATION]: "S-SUM",
    [Token.X_SUMMATION]: "S-SUM",
    [Token.NUMBER_OF_DATA]: "S-SUM",
    [Token.Y_SQUARE_SUMMATION]: "S-SUM",
    [Token.Y_SUMMATION]: "S-SUM",
    [Token.XY_SUMMATION]: "S-SUM",
    [Token.X_SQUARE_Y_SUMMATION]: "S-SUM",
    [Token.X_CUBE_SUMMATION]: "S-SUM",
    [Token.X_QUADRUPLE_SUMMATION]: "S-SUM",
    [Token.X_MEAN]: "S-VAR",
    [Token.X_STANDARD_DEVIATION]: "S-VAR",
    [Token.X_SAMPLE_STANDARD_DEVIATION]: "S-VAR",
    [Token.Y_MEAN]: "S-VAR",
    [Token.Y_STANDARD_DEVIATION]: "S-VAR",
    [Token.Y_SAMPLE_STANDARD_DEVIATION]: "S-VAR",
    [Token.REGRESSION_CONSTANT_A]: "S-VAR",
    [Token.REGRESSION_COEFFICIENT_B]: "S-VAR",
    [Token.REGRESSION_COEFFICIENT_C]: "S-VAR",
    [Token.REGRESSION_CORRELATION_COEFFICIENT_R]: "S-VAR",
    [Token.X_ESTIMATED_VALUE]: "S-VAR",
    [Token.X1_ESTIMATED_VALUE]: "S-VAR",
    [Token.X2_ESTIMATED_VALUE]: "S-VAR",
    [Token.Y_ESTIMATED_VALUE]: "S-VAR",
    [Token.X_MINIMUM]: "S-VAR",
    [Token.X_MAXIMUM]: "S-VAR",
    [Token.Y_MINIMUM]: "S-VAR",
    [Token.Y_MAXIMUM]: "S-VAR",
    [Token.PROTON_MASS]: "CONST",
    [Token.NEUTRON_MASS]: "CONST",
    [Token.ELECTRON_MASS]: "CONST",
    [Token.MUON_MASS]: "CONST",
    [Token.BOHR_RADIUS]: "CONST",
    [Token.PLANCK_CONSTANT]: "CONST",
    [Token.NUCLEAR_MAGNETON]: "CONST",
    [Token.BOHR_MAGNETON]: "CONST",
    [Token.PLANCK_CONSTANT_REDUCED]: "CONST",
    [Token.FINE_STRUCTURE_CONSTANT]: "CONST",
    [Token.CLASSICAL_ELECTRON_RADIUS]: "CONST",
    [Token.ELECTRON_COMPTON_WAVELENGTH]: "CONST",
    [Token.PROTON_GYROMAGNETIC_RATIO]: "CONST",
    [Token.PROTON_COMPTON_WAVELENGTH]: "CONST",
    [Token.NEUTRON_COMPTON_WAVELENGTH]: "CONST",
    [Token.RYDBERG_CONSTANT]: "CONST",
    [Token.ATOMIC_MASS_CONSTANT]: "CONST",
    [Token.PROTON_MAGNETIC_MOMENT]: "CONST",
    [Token.ELECTRON_MAGNETIC_MOMENT]: "CONST",
    [Token.NEUTRON_MAGNETIC_MOMENT]: "CONST",
    [Token.MUON_MAGNETIC_MOMENT]: "CONST",
    [Token.FARADAY_CONSTANT]: "CONST",
    [Token.ELEMENTARY_CHARGE]: "CONST",
    [Token.AVOGADRO_CONSTANT]: "CONST",
    [Token.BOLTZMANN_CONSTANT]: "CONST",
    [Token.MOLAR_VOLUME_OF_IDEAL_GAS]: "CONST",
    [Token.MOLAR_GAS_CONSTANT]: "CONST",
    [Token.SPEED_OF_LIGHT_IN_VACUUM]: "CONST",
    [Token.FIRST_RADIATION_CONSTANT]: "CONST",
    [Token.SECOND_RADIATION_CONSTANT]: "CONST",
    [Token.STEFAN_BOLTZMANN_CONSTANT]: "CONST",
    [Token.VACUUM_PERMITTIVITY]: "CONST",
    [Token.VACUUM_PERMEABILITY]: "CONST",
    [Token.MAGNETIC_FLUX_QUANTUM]: "CONST",
    [Token.STANDARD_ACCERLATION_OF_GRAVITY]: "CONST",
    [Token.CONDUCTANCE_QUANTUM]: "CONST",
    [Token.CHARACTERISTIC_IMPEDANCE_OF_VACUUM]: "CONST",
    [Token.KELVIN_CELSIUS_SCALE_OFFSET]: "CONST",
    [Token.GRAVITATIONAL_CONSTANT]: "CONST",
    [Token.STANDARD_ATMOSPHERE]: "CONST",
    [Token.CLEAR_MEMORY]: "CLR",
    [Token.CLEAR_STATISTICS]: "CLR",
    [Token.DEGREE_MODE]: "SETUP",
    [Token.RADIAN_MODE]: "SETUP",
    [Token.GRADIAN_MODE]: "SETUP",
    [Token.FIXED_POINT]: "SETUP",
    [Token.SCIENTIFIC_NOTATION]: "SETUP",
    [Token.NORMAL_NOTATION]: "SETUP",
    [Token.FREQUENCY_ON]: "SETUP",
    [Token.FREQUENCY_OFF]: "SETUP",
    [Token.QUERY_INPUT]: "Prog",
    [Token.DISPLAY]: "Prog",
    [Token.CONDITIONAL]: "Prog",
    [Token.EQUAL]: "Prog",
    [Token.NOT_EQUAL]: "Prog",
    [Token.GREATER_THAN]: "Prog",
    [Token.LESS_THAN]: "Prog",
    [Token.GREATER_THAN_OR_EQUAL]: "Prog",
    [Token.LESS_THAN_OR_EQUAL]: "Prog",
    [Token.GOTO]: "Prog",
    [Token.LABEL]: "Prog",
    [Token.WHILE]: "Prog",
    [Token.WHILE_END]: "Prog",
    [Token.NEXT]: "Prog",
    [Token.BREAK]: "Prog",
    [Token.FOR]: "Prog",
    [Token.TO]: "Prog",
    [Token.STEP]: "Prog",
    [Token.ELSE]: "Prog",
    [Token.IF_END]: "Prog",
    [Token.IF]: "Prog",
    [Token.THEN]: "Prog",
} as const;